import { Button } from '@/components/ui/button';

import { useDraft } from '../builder/useDraftContext';
import {
  deleteTransition,
  findStep,
  setTransitionField,
} from '../builder/draftOps';
import type { BuilderSelection } from '../builder/selection';
import { useFieldError } from '../builder/useFieldError';
import { CommitField } from './CommitField';

/**
 * One transition: where it goes, and when.
 *
 * The condition is the field that matters. It is prose the runtime evaluates to
 * decide which way a task moves, it is required before the workflow can be
 * published, and the builder deliberately creates transitions without one —
 * inventing a condition would put words in the author's mouth that publication
 * then accepts.
 */
/**
 * The half of a transition that no runtime reads yet.
 *
 * Nothing follows a transition today: a task is placed wherever the board says —
 * `TaskService.move_task` consults no transition and documents that it
 * "deliberately enforces no legality" — and the durable execution runs one step
 * without choosing a successor. So a condition and a traversal limit are
 * authored, validated for shape, stored, and read by nothing.
 *
 * Said on both fields rather than one, and in one place rather than two, because
 * an author who sets a limit has otherwise been told by implication that a loop
 * is now bounded. The second clause is the load-bearing half: "not enforced"
 * alone reads as a defect, where naming what *does* move a task describes the
 * stage the product is at.
 */
const NOT_EVALUATED =
  'Recorded in the definition — nothing evaluates it yet; a task moves when someone drags it on the board.';

export function TransitionInspector({
  stepId,
  transitionKey,
  onSelect,
}: {
  stepId: string;
  transitionKey: string;
  onSelect: (selection: BuilderSelection) => void;
}) {
  const { draft, edit } = useDraft();
  const { resolve: resolveWhen } = useFieldError('when');
  const { resolve: resolveTraversalLimit } = useFieldError('traversal_limit');
  const step = findStep(draft, stepId);
  const transition = step?.transitions.find(
    (candidate) => candidate.key === transitionKey,
  );

  if (!step || !transition) return null;

  return (
    <div className="flex flex-col gap-5">
      <dl className="flex flex-col gap-2 text-sm">
        <div className="flex items-baseline gap-2">
          <dt className="text-muted-foreground w-16 shrink-0 text-xs">From</dt>
          <dd className="text-foreground min-w-0 truncate">{step.name}</dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="text-muted-foreground w-16 shrink-0 text-xs">To</dt>
          <dd className="text-foreground font-brand-mono min-w-0 truncate">
            {transition.to}
          </dd>
        </div>
      </dl>

      <CommitField
        label="Condition"
        value={transition.when}
        multiline
        rows={4}
        placeholder="The specification is complete and has no blocking ambiguity."
        required
        field="when"
        onCommit={(when) => {
          edit(setTransitionField(stepId, transitionKey, 'when', when));
          resolveWhen();
        }}
        hint={`When this transition should be taken. Required before publishing. ${NOT_EVALUATED}`}
      />

      <CommitField
        label="Label"
        value={transition.label ?? ''}
        placeholder="Shown on the arrow"
        onCommit={(label) =>
          edit(
            setTransitionField(stepId, transitionKey, 'label', label || null),
          )
        }
      />

      <CommitField
        label="Traversal limit"
        value={transition.traversalLimit?.toString() ?? ''}
        placeholder="No limit"
        field="traversal_limit"
        onCommit={(raw) => {
          const trimmed = raw.trim();
          if (trimmed === '') {
            edit(
              setTransitionField(stepId, transitionKey, 'traversalLimit', null),
            );
            resolveTraversalLimit();
            return;
          }
          const parsed = Number(trimmed);
          // Refused rather than coerced: the service rejects a non-positive
          // limit, and silently turning "0" into "no limit" would mean the user
          // asked for one thing and got another.
          //
          // The refusal is why the mark is withdrawn *after* the edit lands
          // rather than on entry to this handler: typing "abc" changes nothing,
          // so the service's complaint about the stored value still stands, and
          // clearing it here would drop a problem that is still true.
          if (!Number.isInteger(parsed) || parsed < 1) return;
          edit(
            setTransitionField(stepId, transitionKey, 'traversalLimit', parsed),
          );
          resolveTraversalLimit();
        }}
        hint={`How many times this transition may be taken. ${NOT_EVALUATED}`}
      />

      <CommitField
        label="Key"
        value={transition.key}
        mono
        onCommit={(next) => {
          const trimmed = next.trim();
          if (!trimmed || trimmed === transition.key) return;
          // Unique within its source step only — two steps may each have a
          // `to_review`.
          if (step.transitions.some((t) => t.key === trimmed)) return;
          edit(setTransitionField(stepId, transitionKey, 'key', trimmed));
          onSelect({ kind: 'transition', stepId, key: trimmed });
        }}
      />

      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          edit(deleteTransition(stepId, transitionKey));
          onSelect({ kind: 'step', id: stepId });
        }}
      >
        Remove this transition
      </Button>
    </div>
  );
}
